import React, { useEffect, useState } from 'react';
import Radio from './Radio';
import Button from './Button';
import Select from './Select';
import InputField from './InputField';
import { Link, useNavigate } from 'react-router-dom';
import Cross from '../assets/svgs/Cross';
import { buyerTypes } from '../utils/constants';
import useReCaptcha, { RecaptchaComponent } from '../utils/recaptcha';
import api from '../services/api';

export const EnquireForm = ({ toggleEnquireForm = () => { }, packageId = null, lotId = null }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        postCode: '',
        mobilePhone: '',
        // Temporarily disabled fields
        // findAbout: findOptions[0],
        // buyTimeline: buyOptions[0],
        buyerType: 'Buyer Type',
        howDidYouHearAboutUs: 'How did you hear about us?',
        // message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const { recaptchaRef, executeRecaptcha } = useReCaptcha();
    const [enquireSource, setEnquireSource] = useState([
        'Billboards',
        'Builder Referral',
        'Digital H&L Collections Book',
        'Display Village',
        'Facebook',
        'Family/Friends',
        'Google',
        'Instagram',
        'Local Knowledge',
        'Local Paper',
        'Newsletter',
        'Newspaper Metro',
        'Openlot.com.au',
        'Other',
        'Radio',
        'Signage',
        'Website',
        'realestate.com.au'
    ]);

    useEffect(() => {
        const fetchProject = async () => {
            const project = await api.getProject();
            if (project.frontendEnquirySources && project.frontendEnquirySources.length) {
                setEnquireSource(project.frontendEnquirySources.map(x => x.name));
            }
        }
        fetchProject();
    }, []);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Postcode validation (Australian format)
        if (formData.postCode) {
            const postcodeRegex = /^[0-9]{4}$/;
            if (!postcodeRegex.test(formData.postCode)) {
                newErrors.postCode = 'Please enter a valid 4-digit Australian postcode';
            }
        } else {
            newErrors.postCode = 'Please enter a valid 4-digit Australian postcode';
        }

        // Phone validation (basic)
        const phoneRegex = /^[0-9+\s()-]{8,15}$/;
        if (!phoneRegex.test(formData.mobilePhone)) {
            newErrors.mobilePhone = 'Please enter a valid phone number';
        }

        // Required fields
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        // Buyer Type validation
        if (formData.buyerType === 'Buyer Type') {
            newErrors.buyerType = 'Please select a buyer type';
        }

        // How did you hear about us validation
        if (formData.howDidYouHearAboutUs === 'How did you hear about us?') {
            newErrors.howDidYouHearAboutUs = 'Please select how you heard about us';
        }

        // Terms validation
        if (!isChecked) {
            newErrors.terms = 'You must agree to the terms and conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);

            try {
                // Get reCAPTCHA token using the new method with action parameter
                const token = await executeRecaptcha('enquire_form');

                // Create form data for submission
                const formDataToSubmit = new URLSearchParams();
                formDataToSubmit.append('firstName', formData.firstName);
                formDataToSubmit.append('lastName', formData.lastName);
                formDataToSubmit.append('email', formData.email);
                formDataToSubmit.append('postCode', formData.postCode);
                formDataToSubmit.append('buyerType', formData.buyerType);
                formDataToSubmit.append('mobilePhone', formData.mobilePhone);
                formDataToSubmit.append('howDidYouHearAboutUs', formData.howDidYouHearAboutUs);

                // Add packageId and lotId if available
                if (packageId) {
                    formDataToSubmit.append('packageId', packageId);
                }
                if (lotId) {
                    formDataToSubmit.append('lotId', lotId);
                }

                // Temporarily disabled fields
                // formDataToSubmit.append('findAbout', formData.findAbout);
                // formDataToSubmit.append('buyTimeline', formData.buyTimeline);
                // formDataToSubmit.append('message', formData.message);
                // formDataToSubmit.append('formSource', 'Main');

                // Add reCAPTCHA token
                formDataToSubmit.append('g-recaptcha-response', token);

                // API endpoint from environment variable
                const apiUrl = `${process.env.REACT_APP_API_URL}/enquire`;

                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: formDataToSubmit,
                });

                if (response.ok) {
                    // Success handling
                    setIsSubmitted(true);
                    setIsSubmitting(false);

                    // Reset form
                    setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        postCode: '',
                        mobilePhone: '',
                        // Temporarily disabled fields
                        // findAbout: findOptions[0],
                        // buyTimeline: buyOptions[0],
                        buyerType: 'Buyer Type',
                        howDidYouHearAboutUs: 'How did you hear about us?',
                        // message: ''
                    });
                    setIsChecked(false);

                    // Reset form after 3 seconds or close it
                    setTimeout(() => {
                        toggleEnquireForm();
                        navigate('/thank-you');
                    }, 3000);
                } else {
                    const errorData = await response.json();
                    console.error('Form submission error:', errorData);
                    setIsSubmitting(false);
                    setErrors(prev => ({
                        ...prev,
                        general: errorData.message || 'Something went wrong. Please try again.'
                    }));
                }
            } catch (error) {
                console.error('Form submission error:', error);
                setIsSubmitting(false);
                setErrors(prev => ({
                    ...prev,
                    general: 'reCAPTCHA verification failed. Please try again.'
                }));
            }
        }
    };

    return (
        <div className='bg-gableGreen fixed h-screen w-full left-0 right-0 top-0 z-50 px-6 sm:px-10 xl:px-59 pt-10 overflow-y-auto'>
            <div className='w-full'>
                <div onClick={toggleEnquireForm} className='ml-auto cursor-pointer w-fit'>
                    <Cross className='w-5 sm:w-6 xl:w-8' />
                </div>
            </div>
            <div className='w-full sm:w-85 lg:w-70 mx-auto mt-5 sm:mt-20 2xl:mt-32 pb-10'>
                <h1 className='text-honeysuckle font-tobias font-medium text-4xl xl:text-5xl'>Contact me about Banyan Hill</h1>

                {isSubmitted ? (
                    <div className='mt-10 p-6 bg-honeysuckle bg-opacity-20 rounded-lg text-center'>
                        <h2 className='text-honeysuckle text-2xl mb-3'>Thank you for your enquiry!</h2>
                        <p className='text-honeysuckle'>We'll be in touch with you shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className='flex flex-col gap-6 sm:gap-10 mt-10'>
                        <RecaptchaComponent ref={recaptchaRef} />

                        <div className='flex flex-col sm:flex-row items-center gap-6 sm:gap-10'>
                            <div className='w-full'>
                                <InputField
                                    placeholder='First Name'
                                    variant='honeysuckle'
                                    value={formData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                />
                                {errors.firstName && <span className='text-red-300 text-xs mt-1'>{errors.firstName}</span>}
                            </div>
                            <div className='w-full'>
                                <InputField
                                    placeholder='Last Name'
                                    variant='honeysuckle'
                                    value={formData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                />
                                {errors.lastName && <span className='text-red-300 text-xs mt-1'>{errors.lastName}</span>}
                            </div>
                        </div>
                        <div className='flex flex-col sm:flex-row items-center gap-6 sm:gap-10'>
                            <div className='w-full'>
                                <InputField
                                    placeholder='Email'
                                    variant='honeysuckle'
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                />
                                {errors.email && <span className='text-red-300 text-xs mt-1'>{errors.email}</span>}
                            </div>
                            <div className='w-full'>
                                <InputField
                                    placeholder='Postcode (4 digits)'
                                    variant='honeysuckle'
                                    value={formData.postCode}
                                    onChange={(e) => handleInputChange('postCode', e.target.value)}
                                    maxLength="4"
                                />
                                {errors.postCode && <span className='text-red-300 text-xs mt-1'>{errors.postCode}</span>}
                            </div>
                        </div>
                        {/* Temporarily disabled fields */}
                        {/*
                        <div className='flex flex-col sm:flex-row items-center gap-6 sm:gap-10 z-20'>
                            <Select
                                defaultValue={formData.findAbout}
                                options={findOptions}
                                onChange={(value) => handleInputChange('findAbout', value)}
                            />
                            <Select
                                defaultValue={formData.buyTimeline}
                                options={buyOptions}
                                onChange={(value) => handleInputChange('buyTimeline', value)}
                            />
                        </div>
                        */}
                        <div className='flex flex-col sm:flex-row items-center gap-6 sm:gap-10'>
                            <div className='w-full'>
                                <Select
                                    defaultValue={formData.buyerType}
                                    options={[...buyerTypes]}
                                    onChange={(value) => handleInputChange('buyerType', value)}
                                    variant="enhanced"
                                    mobileTitle="Select Buyer Type"
                                />
                                {errors.buyerType && <span className='text-red-300 text-xs mt-1'>{errors.buyerType}</span>}
                            </div>
                            <div className='w-full'>
                                <InputField
                                    placeholder='Phone'
                                    variant='honeysuckle'
                                    value={formData.mobilePhone}
                                    onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                                />
                                {errors.mobilePhone && <span className='text-red-300 text-xs mt-1'>{errors.mobilePhone}</span>}
                            </div>
                        </div>
                        <div className='flex flex-col sm:flex-row items-center gap-6 sm:gap-10'>
                            <div className='w-full'>
                                <Select
                                    defaultValue={formData.howDidYouHearAboutUs}
                                    options={enquireSource}
                                    onChange={(value) => handleInputChange('howDidYouHearAboutUs', value)}
                                    variant="enhanced"
                                    mobileTitle="How did you hear about us?"
                                />
                                {errors.howDidYouHearAboutUs && <span className='text-red-300 text-xs mt-1'>{errors.howDidYouHearAboutUs}</span>}
                            </div>
                            <div className='w-full'>
                                {/* Empty div to maintain layout balance */}
                            </div>
                        </div>
                        <div className="flex gap-6 items-center">
                            <div onClick={() => {
                                setIsChecked(!isChecked);
                                if (errors.terms) {
                                    setErrors(prev => ({ ...prev, terms: '' }));
                                }
                            }}>
                                <Radio variant='honeysuckle' checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
                            </div>
                            <label className="text-xs sm:text-sm font-mori text-honeysuckle">I have read and agree with the <Link className='underline' to="/our-policies" target="_blank" rel="noopener noreferrer">Terms & Conditions*</Link></label>
                        </div>
                        {errors.terms && <span className='text-red-300 text-xs mt-1'>{errors.terms}</span>}

                        {errors.general && <div className='text-red-300 text-sm mt-2 text-center'>{errors.general}</div>}
                        <div className='mt-6 sm:mt-10'>
                            <Button
                                title={isSubmitting ? 'Submitting...' : 'Submit'}
                                className='sm:!px-12'
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            />
                        </div>
                    </form>
                )}
            </div>
        </div>
    )
}

export default EnquireForm